import { useMousePosition } from '../../hooks/useMousePosition';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Clock, Sparkles, Wallet } from 'lucide-react';
import GeometricBackground from './GeometricBackground';
import FloatingCard from './FloatingCard';

function OrbitalRing({ size, rotation, color, speed }) {
  const mouse = useMousePosition();
  return (
    <div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2"
      style={{
        width: size,
        height: size,
        transform: `translate(-50%, -50%) rotateX(${70 + mouse.y * 8}deg) rotateZ(${rotation + mouse.x * 5}deg)`,
        transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        borderColor: color,
      }}
    >
      <div
        className="absolute w-3 h-3 rounded-full bg-primary shadow-[0_0_16px_hsl(var(--primary))]"
        style={{ top: '0%', left: '50%', transform: 'translate(-50%, -50%)', animation: `orbit-${size} ${speed}s linear infinite` }}
      />
    </div>
  );
}

const trustIndicators = [
  { icon: ShieldCheck, value: '100%', label: 'Seguro' },
  { icon: Clock, value: '24/7', label: 'Disponível' },
  { icon: Sparkles, value: 'IA', label: 'Integrada' },
  { icon: Wallet, value: 'R$9,90', label: 'A partir de' },
];

export default function HeroSection() {
  const navigate = useNavigate();
  const mouse = useMousePosition();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 pb-24" data-testid="hero-section">
      <GeometricBackground />

      {/* 3D Orbital System */}
      <div
        className="absolute right-[8%] top-1/2 -translate-y-1/2 hidden lg:block"
        style={{
          perspective: '1200px',
          transform: `translateY(-50%) rotateX(${mouse.y * -6}deg) rotateY(${mouse.x * 6}deg)`,
          transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div className="relative w-[420px] h-[420px]">
          <OrbitalRing size={420} rotation={0} color="hsl(var(--primary)/0.30)" speed={20} />
          <OrbitalRing size={340} rotation={45} color="hsl(var(--primary)/0.40)" speed={15} />
          <OrbitalRing size={260} rotation={90} color="hsl(var(--primary)/0.50)" speed={10} />

          {/* Chat card at the center — focal point of the orbital system */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[260px] z-10"
            style={{ transform: 'translate(-50%, -50%)' }}
          >
            <div className="absolute -inset-6 rounded-3xl bg-primary/20 blur-2xl" />
            <FloatingCard delay={1} intensity={0.4} className="w-full">
              <div className="rounded-2xl border-2 border-primary/30 bg-card/95 backdrop-blur-xl p-4 shadow-[0_0_60px_-10px_hsl(var(--primary)/0.6)]">
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border/40">
                  <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-emerald-500/30">A</div>
                  <div>
                    <div className="text-sm font-bold text-foreground">Aretê Bot</div>
                    <div className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      online
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-end">
                    <div className="bg-emerald-100 text-emerald-950 rounded-2xl rounded-tr-sm px-3 py-2 text-xs max-w-[85%]">
                      Qual meu saldo?
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-primary/10 text-foreground rounded-2xl rounded-tl-sm px-3 py-2 text-xs max-w-[90%]">
                      <div className="font-semibold text-primary mb-0.5">Saldo: R$ 3.450,00</div>
                      <div className="text-[10px] text-muted-foreground">Atualizado agora</div>
                    </div>
                  </div>
                </div>
              </div>
            </FloatingCard>
          </div>
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-12 items-center">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/8 px-4 py-1.5 mb-8 animate-fade-in-up">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            <span className="text-xs font-medium text-primary">Controle financeiro pessoal com IA e WhatsApp</span>
          </div>

          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-foreground leading-[1.05] mb-6 animate-fade-in-up"
            style={{ animationDelay: '0.1s' }}
            data-testid="hero-title"
          >
            Suas finanças no <span className="text-primary">WhatsApp</span>
          </h1>

          <p
            className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-lg animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            Registre gastos por mensagem, envie fotos de recibos, consulte seu saldo e receba relatórios automáticos. A Aretê une controle financeiro, inteligência artificial e a praticidade do WhatsApp.
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-12 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <button
              onClick={() => navigate('/login')}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl font-semibold px-8 py-3.5 transition-all active:scale-95 shadow-[0_0_30px_-5px_hsl(var(--primary)/0.4)] hover:shadow-[0_0_40px_-5px_hsl(var(--primary)/0.6)]"
            >
              Começar Agora
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => document.getElementById('funcionalidades')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground font-medium px-6 py-3.5 rounded-xl border border-border/60 hover:border-border transition-all"
              data-testid="hero-cta-secondary"
            >
              Ver Funcionalidades
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            {trustIndicators.map((t) => {
              const Icon = t.icon;
              return (
                <div key={t.label} className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-foreground tabular-nums">{t.value}</div>
                    <div className="text-[11px] text-muted-foreground">{t.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
}
